import { useEffect, type ReactNode } from 'react';
import { AppButton } from './Controls';

/**
 * Диалог-форма: заголовок, поля, пара кнопок. Порт `AppFormDialog` Android-версии.
 *
 * Пятнадцать файлов собирали диалог вручную, и в двух углы оказались системными,
 * а не нашими. Здесь скругление, фон и отступы заданы один раз.
 *
 * Фон диалога прокинут в `--field-label-backdrop`: метка поля «вырезает» рамку своим
 * фоном, и на другом фоне вокруг неё появилась бы светлая полоса.
 */
export function AppFormDialog({
  title,
  children,
  onDismiss,
  confirm,
  dismissLabel = 'Отмена',
  onDismissClick,
}: {
  title: string;
  children: ReactNode;
  onDismiss: () => void;
  confirm?: { label: string; onClick: () => void; enabled?: boolean };
  dismissLabel?: string;
  /**
   * Своё действие на левой кнопке вместо простого закрытия — обычно «Удалить»
   * у записи, которую правят. Третьей кнопки в диалоге нет намеренно: на телефоне
   * три кнопки в ряд не помещаются, и Android решает это так же (`CaseLinkDialog`).
   */
  onDismissClick?: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <h2 className="dialog__title">{title}</h2>
        <div className="dialog__body">{children}</div>
        <div className="dialog__buttons">
          <AppButton variant="text" onClick={onDismissClick ?? onDismiss}>
            {dismissLabel}
          </AppButton>
          {confirm ? (
            <AppButton
              variant="text"
              disabled={confirm.enabled === false}
              onClick={() => {
                if (confirm.enabled !== false) confirm.onClick();
              }}
            >
              {confirm.label}
            </AppButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Строка «подпись — значение», открывающая выбор. */
export function AppPickerRow({
  label,
  value,
  onClick,
  action = false,
}: {
  label: string;
  value: string;
  onClick: () => void;
  /**
   * Строка-приглашение («Новый клиент») набирается цветом действия — так же, как
   * `AppSectionRow` с `action` ([D-36](docs/DECISIONS.md)): иначе она теряется среди
   * готовых вариантов и её просто не замечают.
   */
  action?: boolean;
}) {
  return (
    <button
      type="button"
      className={`picker-row ${action ? 'picker-row--action' : ''}`}
      onClick={onClick}
    >
      <span className="picker-row__label">{label}</span>
      <span className="picker-row__value">{value}</span>
    </button>
  );
}
